'use client'
import React, { useState, useEffect } from "react";
import { fetchAlerts } from "../core/alert-service";
import "./ServiceAlert.css";

interface Alert {
    Message: string;
    Severity: string;
    EndDateTime: string | number | Date;
    Title: string;
}

interface ServiceAlertProps {
    accessToken: string;
}

const ServiceAlert: React.FC<ServiceAlertProps> = ({ accessToken }) => {
    const [alerts, setAlerts] = useState<Alert[]>([]);
    const [currentAlertIndex, setCurrentAlertIndex] = useState(0);

    useEffect(() => {
        const getAlerts = async () => {
            try {
                if (accessToken) {
                    const data = await fetchAlerts(accessToken);
                    const filteredAlerts = data.filter((alert: Alert) => new Date(alert.EndDateTime) > new Date());
                    setAlerts(filteredAlerts);
                } else {
                    console.error("Access token is null or undefined");
                }
            } catch (error) {
                console.error("Error fetching alerts:", error);
            }
        };

        getAlerts();

        const interval = setInterval(async () => {
            await getAlerts();
        }, 60000); // Auto-refresh every 60 seconds

        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        setCurrentAlertIndex(0); // Reset index when alerts change

        if (alerts.length > 1) {
            const interval = setInterval(() => {
                setCurrentAlertIndex((prevIndex) => (prevIndex + 1) % alerts.length);
            }, 10000); // Change alert every 10 seconds

            return () => clearInterval(interval);
        }

        // Always clear interval if alerts.length <= 1
        return () => { };
    }, [alerts]);

    if (alerts.length === 0) {
        return <div></div>;
    }

    const currentAlert = alerts[currentAlertIndex];

    return (
        <div className="bg-red-100 text-red-700 p-4 rounded flex flex-col items-left overflow-hidden">
            {currentAlert ? (
                <div className={alerts.length > 1 ? "fade" : ""}>
                    <div className="flex items-start">
                        {currentAlert.Severity.toUpperCase() === "1 - HIGH" ? (
                            <svg
                                className="mr-2 feather feather-alert-circle mt-0 min-w-6 min-h-6"
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <circle cx="12" cy="12" r="10"></circle>
                                <line x1="12" y1="8" x2="12" y2="12"></line>
                                <line x1="12" y1="16" x2="12.01" y2="16"></line>
                            </svg>
                        ) : (
                            <div className="mr-2 w-0 h-6"></div>
                        )}
                        <div className={`${currentAlert.Severity.toUpperCase() === "1 - HIGH" ? "" : "ml-0"} flex flex-col`}>
                            <h3 className="font-semibold">{currentAlert.Title}</h3>
                            <p>
                                <small className="block">{currentAlert.Message}</small>
                            </p>
                        </div>
                    </div>
                    {alerts.length > 1 && (
                        <div className="flex mt-2 items-center justify-center">
                            {alerts.map((_, index) => (
                                <div
                                    key={index}
                                    className={`w-2 h-2 rounded-full mx-1 cursor-pointer ${index === currentAlertIndex ? "bg-red-700" : "bg-red-300"}`}
                                    onClick={() => setCurrentAlertIndex(index)}
                                ></div>
                            ))}
                        </div>
                    )}
                </div>
            ) : null}
        </div>
    );
};

export default ServiceAlert;